import React, { FC, ComponentType } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { scale } from '@components/frame';
import Button from '@styles/buttons';
import Svg from '@styles/icons/svg';
import { getBoxShadowStyle } from '@styles/shadow';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { InlineErrorFallbackSize } from './types';
import type { ErrorFallbackProps, InlineErrorFallbackProps } from './types';

const InlineErrorFallback: FC<InlineErrorFallbackProps> = ({ C, onRetry, compact, size }) => {
  // compact is a legacy alias for InlineErrorFallbackSize.Compact
  const resolvedSize: InlineErrorFallbackSize = size ?? (compact ? InlineErrorFallbackSize.Compact : InlineErrorFallbackSize.Full);
  const isCompact = resolvedSize === InlineErrorFallbackSize.Compact;
  const isMicro = resolvedSize === InlineErrorFallbackSize.Micro;

  const boxShadowStyles = getBoxShadowStyle({ type: 'Field', C });
  const iconSize = scale(isMicro ? 18 : isCompact ? 28 : 48);
  const titleFont = isCompact ? font.Body : font['Body Large'];
  const messageFont = isCompact ? font.Footnote : font.Body;

  const title = (
    <Text testID="inline-error-fallback-title" style={[titleFont, !isCompact && styles.title, { color: C('Label/Light/Error') }]}>
      {i18n.t('Something went wrong')}
    </Text>
  );
  const message = (
    <Text testID="inline-error-fallback-message" style={[messageFont, !isCompact && styles.message, { color: C('Label/Light/Secondary') }]}>
      {i18n.t('Please try again later')}
    </Text>
  );
  const retry = (
    <Button
      variant="S"
      type="Only Text Link"
      onPress={onRetry}
      testID="inline-error-fallback-retry"
      customStyle={resolvedSize === InlineErrorFallbackSize.Full ? styles.retry : undefined}
    >
      {i18n.t('Try Again')}
    </Button>
  );
  const icon = <Svg icon="errorBig" width={iconSize} height={iconSize} color="Label/Light/Error" />;

  // micro stays content-sized with no card/shadow so it can drop into a small inline slot without breaking the surrounding layout
  if (isMicro) {
    return (
      <View testID="inline-error-fallback" style={styles.microContainer}>
        {icon}
        {retry}
      </View>
    );
  }

  return (
    <View
      testID="inline-error-fallback"
      style={[boxShadowStyles, isCompact ? styles.compactContainer : styles.container, { backgroundColor: C('Background/Light/Field') }]}
    >
      {icon}
      {isCompact ? (
        <>
          <View style={styles.compactTextColumn}>
            {title}
            {message}
          </View>
          {retry}
        </>
      ) : (
        <>
          {title}
          {message}
          {retry}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: scale(120),
    justifyContent: 'center',
    alignItems: 'center',
    padding: scale(16),
    borderRadius: scale(SCREEN_BORDER_RADIUS),
  },
  title: {
    marginTop: scale(8),
    textAlign: 'center',
  },
  message: {
    marginTop: scale(4),
    textAlign: 'center',
  },
  retry: {
    marginTop: scale(8),
  },
  compactContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: scale(12),
    borderRadius: scale(SCREEN_BORDER_RADIUS),
    gap: scale(12),
  },
  compactTextColumn: {
    flex: 1,
  },
  microContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(6),
    paddingVertical: scale(4),
  },
});

// three size peers — all named so the module surface stays symmetric
export const DefaultInlineErrorFallback = ColorWrapper(InlineErrorFallback);

// size-specific adapters with stable module-level identities, so the boundary doesn't mint a new fallback component each render
export const CompactInlineErrorFallback: ComponentType<ErrorFallbackProps> = (props) => <DefaultInlineErrorFallback {...props} size={InlineErrorFallbackSize.Compact} />;

export const MicroInlineErrorFallback: ComponentType<ErrorFallbackProps> = (props) => <DefaultInlineErrorFallback {...props} size={InlineErrorFallbackSize.Micro} />;
